// @flow
import getDateAsMoment from 'utils/getDateAsMoment';
import { EMPLOYMENT_TYPE, JOB_FIELD_NAMES } from './constants.job';
import {
  getBaseDefaultValues,
  getDefaultValueForMemberId,
} from 'pages/AffordaWagesAndIncome/utils';
import { PAY_FREQUENCY } from 'pages/AffordaWagesAndIncome/constants';

const parseNumber = (value) => {
  const parsedValue = parseFloat(value);
  return isNaN(parsedValue) ? 0 : parsedValue;
};

// Calculate just for payFrequency
const calculateBaseIncome = (memberJob) => {
  const {
    hourlyPay,
    hoursPerWeek,
    weeklyPay,
    biWeeklyPay,
    semiMonthlyPay,
    monthlyPay,
    annualPay,
    payFrequency,
  } = memberJob;

  if (!payFrequency) return 0;

  switch (payFrequency) {
    case PAY_FREQUENCY.hourly:
      return parseNumber(hourlyPay) * parseNumber(hoursPerWeek) * 52;
    case PAY_FREQUENCY.weekly:
      return parseNumber(weeklyPay) * 52;
    case PAY_FREQUENCY.biweekly:
      return parseNumber(biWeeklyPay) * 26;
    case PAY_FREQUENCY.semimonthly:
      return parseNumber(semiMonthlyPay) * 24;
    case PAY_FREQUENCY.monthly:
      return parseNumber(monthlyPay) * 12;
    case PAY_FREQUENCY.annually:
      return parseNumber(annualPay);
    default:
      return 0;
  }
};

// Calculate just for anticipatedIncrease12Months
const calculateAnticipatedIncrease = (memberJob) => {
  const {
    anticipatedIncrease12Months,
    anticipatedIncreaseRate,
    anticipatedIncreaseWhen,
    payFrequency,
    hoursPerWeek,
  } = memberJob;

  if (anticipatedIncrease12Months !== 'yes') return 0;

  const monthsRemaining = 12 - new Date(anticipatedIncreaseWhen).getMonth();
  const weeksRemaining = monthsRemaining * 4.33;

  switch (payFrequency) {
    case PAY_FREQUENCY.hourly:
      return (
        parseNumber(anticipatedIncreaseRate) *
        parseNumber(hoursPerWeek) *
        weeksRemaining
      );
    case PAY_FREQUENCY.weekly:
      return parseNumber(anticipatedIncreaseRate) * weeksRemaining;
    case PAY_FREQUENCY.biweekly:
      return parseNumber(anticipatedIncreaseRate) * (weeksRemaining / 2);
    case PAY_FREQUENCY.semimonthly:
      return (
        parseNumber(anticipatedIncreaseRate) *
        parseNumber(hoursPerWeek) *
        (weeksRemaining / 2)
      );
    case PAY_FREQUENCY.monthly:
      return (
        parseNumber(anticipatedIncreaseRate) *
        parseNumber(hoursPerWeek) *
        (weeksRemaining / 4.33)
      );
    default:
      return 0;
  }
};

const calculateOvertimePay = (memberJob) => {
  const { overtimePay, overtimeHourRate, overtimeAvgHours } = memberJob;

  if (overtimePay !== 'yes') return 0;

  const rate = parseNumber(overtimeHourRate);
  const hours = parseNumber(overtimeAvgHours);

  if (rate === 0 || hours === 0) return 0;

  return rate * hours * 52;
};

const calculateTips = (memberJob) => {
  const {
    tipsAndGratuities,
    tipsAndGratuitiesAllReported,
    receiveTipsAvgWeek,
    receiveTipsAvgUnreported,
  } = memberJob;

  if (tipsAndGratuities !== 'yes') return 0;

  const reportedTips = parseNumber(receiveTipsAvgWeek);
  const unreportedTips = parseNumber(receiveTipsAvgUnreported);

  if (tipsAndGratuitiesAllReported === 'yes') {
    return reportedTips * 52;
  }

  return (reportedTips + unreportedTips) * 52;
};

const calculateBonuses = (memberJob) => {
  const {
    commissionsOrBonuses,
    commissionsPayFrequency,
    commissionsPayFrequencyWeekly,
    commissionsPayFrequencyMonthly,
    commissionsPayFrequencyAnnually,
  } = memberJob;

  if (commissionsOrBonuses !== 'yes' || !commissionsPayFrequency) return 0;

  switch (commissionsPayFrequency) {
    case PAY_FREQUENCY.weekly:
      return parseNumber(commissionsPayFrequencyWeekly) * 52;
    case PAY_FREQUENCY.monthly:
      return parseNumber(commissionsPayFrequencyMonthly) * 12;
    case PAY_FREQUENCY.annually:
      return parseNumber(commissionsPayFrequencyAnnually);
    default:
      return 0;
  }
};

const calculateMilitaryIncome = (memberJob) => {
  const {
    employmentType,
    militaryBranch,
    basePayLongevity,
    proficiencyPay,
    seaAndForeignDutyPay,
    subsistenceAllowance,
    quartersAllowance,
  } = memberJob;

  if (
    employmentType !== EMPLOYMENT_TYPE.military ||
    !militaryBranch ||
    !militaryBranch.length
  )
    return 0;

  const basePay = parseNumber(basePayLongevity);
  const proficiency = parseNumber(proficiencyPay);
  const foreignDuty = parseNumber(seaAndForeignDutyPay);
  const subsistence = parseNumber(subsistenceAllowance);
  const quarters = parseNumber(quartersAllowance);

  return (basePay + proficiency + foreignDuty + subsistence + quarters) * 12;
};

export const calculateGrossAnnualIncome = (memberJob) => {
  const baseIncome = calculateBaseIncome(memberJob);
  const anticipatedIncrease = calculateAnticipatedIncrease(memberJob);
  const overtimePay = calculateOvertimePay(memberJob);
  const tips = calculateTips(memberJob);
  const bonuses = calculateBonuses(memberJob);
  const militaryIncome = calculateMilitaryIncome(memberJob);
  const selfEmployedIncome = parseNumber(memberJob[JOB_FIELD_NAMES.averageNet]);

  return (
    baseIncome +
    anticipatedIncrease +
    overtimePay +
    tips +
    bonuses +
    militaryIncome +
    selfEmployedIncome
  );
};

export const getDefaultValues = (item: Object, members, index) => {
  return {
    id: item.id,
    ...getBaseDefaultValues(item, members, index),
    [JOB_FIELD_NAMES.annualNet]: item.annualNet ?? '',
    [JOB_FIELD_NAMES.annualPay]: item.annualPay ?? '',
    [JOB_FIELD_NAMES.anticipatedIncrease12Months]:
      item.anticipatedIncrease12Months ?? '',
    [JOB_FIELD_NAMES.anticipatedIncreaseRate]:
      item.anticipatedIncreaseRate ?? '',
    [JOB_FIELD_NAMES.anticipatedIncreaseWhen]:
      item.anticipatedIncreaseWhen ?? '',
    [JOB_FIELD_NAMES.averageNet]: item.averageNet ?? '',
    [JOB_FIELD_NAMES.basePayLongevity]: item.basePayLongevity ?? '',
    [JOB_FIELD_NAMES.biWeeklyPay]: item.biWeeklyPay ?? '',
    [JOB_FIELD_NAMES.bonusOrCommissions]: item.bonusOrCommissions ?? '',
    [JOB_FIELD_NAMES.city]: item.city ?? '',
    [JOB_FIELD_NAMES.commissionsOrBonuses]: item.commissionsOrBonuses ?? '',
    [JOB_FIELD_NAMES.commissionsOrBonusesType]:
      item.commissionsOrBonusesType ?? '',
    [JOB_FIELD_NAMES.commissionsPayFrequency]:
      item.commissionsPayFrequency ?? '',
    [JOB_FIELD_NAMES.commissionsPayFrequencyAnnually]:
      item.commissionsPayFrequencyAnnually ?? '',
    [JOB_FIELD_NAMES.commissionsPayFrequencyMonthly]:
      item.commissionsPayFrequencyMonthly ?? '',
    [JOB_FIELD_NAMES.commissionsPayFrequencyWeekly]:
      item.commissionsPayFrequencyWeekly ?? '',
    [JOB_FIELD_NAMES.endDate]: getDateAsMoment(item.endDate),
    [JOB_FIELD_NAMES.doNotWorkEndDate]: getDateAsMoment(item.doNotWorkEndDate),
    [JOB_FIELD_NAMES.doNotWorkStartDate]: getDateAsMoment(
      item.doNotWorkStartDate,
    ),
    [JOB_FIELD_NAMES.doNotWorkEndDate2]: getDateAsMoment(
      item[JOB_FIELD_NAMES.doNotWorkEndDate2],
    ),
    [JOB_FIELD_NAMES.doNotWorkStartDate2]: getDateAsMoment(
      item[JOB_FIELD_NAMES.doNotWorkStartDate2],
    ),
    [JOB_FIELD_NAMES.employerName]: item.employerName ?? '',
    [JOB_FIELD_NAMES.employmentType]: item.employmentType ?? '',
    [JOB_FIELD_NAMES.foreignDuty]: item.foreignDuty ?? '',
    [JOB_FIELD_NAMES.hourlyPay]: item.hourlyPay ?? '',
    [JOB_FIELD_NAMES.hoursPerWeek]: item.hoursPerWeek ?? '',
    [JOB_FIELD_NAMES.layoffPeriod]: item.layoffPeriod ?? [],
    [JOB_FIELD_NAMES.memberId]: getDefaultValueForMemberId(
      item,
      members,
      index,
    ),
    [JOB_FIELD_NAMES.memberName]: 'Clear this field',
    [JOB_FIELD_NAMES.militaryBranch]: item.militaryBranch ?? '',
    [JOB_FIELD_NAMES.monthlyPay]: item.monthlyPay ?? '',
    [JOB_FIELD_NAMES.numDependents]: item.numDependents ?? '',
    [JOB_FIELD_NAMES.numDependentsClaimed]: item.numDependentsClaimed ?? '',
    [JOB_FIELD_NAMES.overtimeAvgHours]: item.overtimeAvgHours ?? '',
    [JOB_FIELD_NAMES.overtimeHourRate]: item.overtimeHourRate ?? '',
    [JOB_FIELD_NAMES.overtimePay]: item.overtimePay ?? '',
    [JOB_FIELD_NAMES.payFrequency]: item.payFrequency ?? '',
    [JOB_FIELD_NAMES.position]: item.position ?? '',
    [JOB_FIELD_NAMES.proficiency]: item.proficiency ?? '',
    [JOB_FIELD_NAMES.proficiencyPay]: item.proficiencyPay ?? '',
    [JOB_FIELD_NAMES.quarters]: item.quarters ?? '',
    [JOB_FIELD_NAMES.quartersAllowance]: item.quartersAllowance ?? '',
    [JOB_FIELD_NAMES.receiveTipsAvgWeek]: item.receiveTipsAvgWeek ?? '',
    [JOB_FIELD_NAMES.receiveTipsAvgUnreported]:
      item.receiveTipsAvgUnreported ?? '',
    [JOB_FIELD_NAMES.seaAndForeignDutyPay]: item.seaAndForeignDutyPay ?? '',
    [JOB_FIELD_NAMES.seasonalWorkWeeks]: item.seasonalWorkWeeks ?? '',
    [JOB_FIELD_NAMES.selfTaxes]: item.selfTaxes ?? '',
    [JOB_FIELD_NAMES.semiMonthlyPay]: item.semiMonthlyPay ?? '',
    [JOB_FIELD_NAMES.startDate]: getDateAsMoment(item.startDate),
    [JOB_FIELD_NAMES.state]: item.state ?? '',
    [JOB_FIELD_NAMES.streetAddress]: item.streetAddress ?? '',
    [JOB_FIELD_NAMES.subsistence]: item.subsistence ?? '',
    [JOB_FIELD_NAMES.subsistenceAllowance]: item.subsistenceAllowance ?? '',
    [JOB_FIELD_NAMES.supervisorName]: item.supervisorName ?? '',
    [JOB_FIELD_NAMES.supervisorPhone]: item.supervisorPhone ?? '',
    [JOB_FIELD_NAMES.tipsAndGratuities]: item.tipsAndGratuities ?? '',
    [JOB_FIELD_NAMES.tipsAndGratuitiesAllReported]:
      item.tipsAndGratuitiesAllReported ?? '',
    [JOB_FIELD_NAMES.tipsAndGratuitiesPerWeek]:
      item.tipsAndGratuitiesPerWeek ?? '',
    [JOB_FIELD_NAMES.tipsAndGratuitiesUnreportedByWeek]:
      item.tipsAndGratuitiesUnreportedByWeek ?? '',
    [JOB_FIELD_NAMES.weeklyPay]: item.weeklyPay ?? '',
    [JOB_FIELD_NAMES.workType]: item.workType ?? '',
    [JOB_FIELD_NAMES.zipCode]: item.zipCode ?? '',
  };
};
